import { Game } from './core/Game.js';
import { SceneManager } from './core/SceneManager.js';
import { MenuScene } from './scenes/MenuScene.js';
import { OptionsScene } from './scenes/OptionsScene.js';
import { StageEditorScene } from './scenes/StageEditorScene.js';
import { FroggerScene } from './games/frogger/FroggerScene.js';
import { SnakeScene } from './games/snake/SnakeScene.js';
import { PipelineScene } from './games/pipeline/PipelineScene.js';
import { TrainSimScene } from './games/trainsim/TrainSimScene.js';
import { PlatformerScene } from './games/platformer/PlatformerScene.js';
import { setParticleQuality } from './games/platformer/ParticleSystem.js';
import { hydrateDrafts } from './services/StageLoader.js';
import { CONFIG } from '../data/index.js';

// `globalThis.__PRODUCTION_BUILD__` is set to the literal `true` by
// esbuild's `define` in scripts/build.js + scripts/build-offline.js.
// See MenuScene.js for the full rationale — referenced inline at the
// call site (rather than cached in a `const`) so esbuild's dead-code
// elimination + tree-shaking can drop the OptionsScene and
// StageEditorScene class modules out of the production bundle.

/**
 * Application entry point.
 * Boots the game, registers scenes, and starts at the menu.
 *
 * PipelineScene stays registered even though its menu card was replaced
 * by Train Simulator — keeping it wired makes it a one-line revert in
 * MenuScene.CARDS to re-enable it later.
 */
async function boot() {
    // Block on webfont loading BEFORE any PIXI text is constructed.
    //
    // Why: assets/fonts/fonts.css declares Poppins with `font-display: swap`,
    // so the browser hands out Arial as the fallback until the woff2 files
    // arrive. PIXI's Text rasterises a glyph atlas the first time a label
    // is created and uses the canvas measureText() bounds at that moment to
    // size its anchor box. If we render the MenuScene cards before Poppins
    // is ready, the anchor (0.5) gets baked against Arial's narrower metrics
    // — when Poppins finally swaps in, the visible letters shift inside the
    // already-positioned bounds and the card labels end up off-centre.
    //
    // Waiting here makes the boot deterministic: PIXI always measures real
    // Poppins, so labels stay perfectly centred on their cards on every load.
    await preloadFonts();

    const game = new Game();
    game.config = CONFIG;
    await game.init();

    // Apply the global particle-quality ceiling (device tier) before any
    // stage spawns emitters, so the very first effect already respects it.
    // Falls back to the schema default ('high') when unset. This runs in
    // production too (where the Settings UI is stripped) so a baked preset
    // value is still honoured.
    setParticleQuality(game.skin?.getSetting?.('shared.settings', 'particleQuality', 'high'));

    const sceneManager = new SceneManager(game);
    game.sceneManager = sceneManager;

    sceneManager.addScene('menu', new MenuScene());
    sceneManager.addScene('frogger', new FroggerScene());
    sceneManager.addScene('snake', new SnakeScene());
    sceneManager.addScene('pipeline', new PipelineScene());
    sceneManager.addScene('trainsim', new TrainSimScene());
    sceneManager.addScene('platformer', new PlatformerScene());

    // Editor-only scenes — registered only in dev builds. The whole
    // Settings + Stage Editor surface is gone from production / offline
    // bundles (no gear button, no password modal, no editor scenes),
    // shaving hundreds of KB off the shipped JS for end-users who
    // shouldn't have access to those tools anyway.
    if (!globalThis.__PRODUCTION_BUILD__) {
        // Editor drafts live in IndexedDB behind a synchronous cache —
        // hydrate it before the editor can possibly mount so "Continue
        // draft" cards and stage restores see the saved work.
        await hydrateDrafts();
        sceneManager.addScene('options', new OptionsScene());
        sceneManager.addScene('stage-editor', new StageEditorScene());
    }

    sceneManager.switchScene('menu');

    console.log(
        `[Game] Running — canvas ${game.width}x${game.height} @ ${window.devicePixelRatio}x`
    );
}

/**
 * Pre-warm every webfont weight the canvas-side UI uses, so PIXI Text
 * never measures against the Arial fallback. We force a `document.fonts.load`
 * for each (weight, family) pair we actually render with — this triggers the
 * fetch + decode of the underlying woff2 even though `font-display: swap`
 * would otherwise let the page paint without it. The second
 * `document.fonts.ready` is a belt-and-braces wait for any other @font-face
 * declarations (Material Symbols, etc.) to settle too.
 *
 * Wrapped in a try/catch with a short timeout so a broken font asset can
 * never wedge the entire game on a black screen.
 */
async function preloadFonts() {
    if (typeof document === 'undefined' || !document.fonts) return;
    const weights = ['500', '700', '800', '900'];
    const loads = weights.map((w) =>
        document.fonts.load(`${w} 16px Poppins`).catch(() => {})
    );
    const timeout = new Promise((resolve) => setTimeout(resolve, 2500));
    try {
        await Promise.race([Promise.all(loads), timeout]);
        await Promise.race([document.fonts.ready, timeout]);
    } catch (err) {
        console.warn('[Boot] Font preload failed:', err);
    }
}

boot().catch((err) => console.error('[Game] Fatal:', err));
